import React, { useEffect, useState } from 'react';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/select';
import type { CreateStructureTaskFormData, CreateStructureTaskFormProps } from './types';

export const CreateStructureTaskForm: React.FC<CreateStructureTaskFormProps> = ({
  structureTypes,
  defaultStructureTypeId,
  initialData,
  submitLabel,
  onSubmit,
  onCancel,
  isLoading = false,
}) => {
  const [formData, setFormData] = useState<CreateStructureTaskFormData>({
    name: '',
    structure_type: defaultStructureTypeId ?? '',
  });

  useEffect(() => {
    if (defaultStructureTypeId) {
      setFormData((prev) => ({ ...prev, structure_type: defaultStructureTypeId }));
    }
  }, [defaultStructureTypeId]);

  useEffect(() => {
    if (!initialData) return;
    setFormData((prev) => ({
      ...prev,
      name: initialData.name ?? '',
      structure_type: initialData.structure_type ?? defaultStructureTypeId ?? '',
    }));
  }, [initialData, defaultStructureTypeId]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.structure_type) return;
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="task-structure-type">Structure Type</Label>
        <Select
          value={formData.structure_type}
          onValueChange={(value) => setFormData((prev) => ({ ...prev, structure_type: value }))}
        >
          <SelectTrigger id="task-structure-type">
            <SelectValue placeholder="Select a structure type..." />
          </SelectTrigger>
          <SelectContent className="max-h-[300px] overflow-y-auto">
            {structureTypes.map((st) => (
              <SelectItem key={st.id} value={st.id}>
                {st.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="task-name">Task Name</Label>
        <Input
          id="task-name"
          placeholder="e.g. Framing, Electrical Rough-In, Drywall..."
          value={formData.name}
          onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
          autoFocus
          required
        />
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="outline" onClick={onCancel} disabled={isLoading}>
          Cancel
        </Button>
        <Button type="submit" disabled={isLoading || !formData.name.trim() || !formData.structure_type}>
          {isLoading ? 'Saving...' : submitLabel ?? 'Create Task'}
        </Button>
      </div>
    </form>
  );
};
